import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  parseRedirectParams,
  validateRedirectParams,
  formatValidationError,
  ValidationResult,
  saveRedirectLocation,
  getRedirectLocation,
  clearRedirectLocation,
} from "./Redirect.utils";
import {
  handleProjectRedirect,
  ProjectRedirectParams,
} from "./RedirectHandlers/ProjectRedirect";
import { APP_ROUTES } from "config";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { useCheckSignedIn } from "helpers/useCheckSignedIn";

/**
 * Hook to parse and validate URL search parameters
 */
export const useRedirectParams = (): ValidationResult => {
  const [searchParams] = useSearchParams();
  const params = parseRedirectParams(searchParams);
  return validateRedirectParams(params);
};

/**
 * Hook to handle the redirect logic based on validated parameters
 */
export const useHandleRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [hasRedirected, setHasRedirected] = useState(false);
  const isSignedIn = useCheckSignedIn();

  useEffect(() => {
    // Prevent multiple redirects
    if (hasRedirected) return;

    // First, check if there's a stored redirect location
    const storedRedirect = getRedirectLocation();

    if (storedRedirect) {
      // We have a stored redirect - user must have just signed in
      // Process the stored redirect and clear it
      try {
        switch (storedRedirect.module) {
          case "project":
            handleProjectRedirect(
              storedRedirect.params as unknown as ProjectRedirectParams,
              navigate,
              dispatch
            );
            setHasRedirected(true);
            clearRedirectLocation();
            break;

          // Future modules can be added here

          default:
            toast(
              <ToastMessage
                toastData={[
                  {
                    title: "Invalid Link",
                    text: "The link you followed is invalid. Please check the URL and try again.",
                  },
                ]}
                className="ToastError"
              />,
              {
                ...toastConfig,
              }
            );
            navigate(APP_ROUTES.homedashboard, { replace: true });
            setHasRedirected(true);
            clearRedirectLocation();
        }
      } catch (error) {
        console.error("Redirect error:", error);
        toast(
          <ToastMessage
            toastData={[
              {
                title: "Error",
                text: "Something went wrong. Please try again or contact support if the issue persists.",
              },
            ]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );
        navigate(APP_ROUTES.homedashboard, { replace: true });
        setHasRedirected(true);
        clearRedirectLocation();
      }
      return;
    }

    // No stored redirect, check URL parameters
    const params = parseRedirectParams(searchParams);
    const validation = validateRedirectParams(params);

    // Handle invalid params
    if (!validation.isValid) {
      const errorMessage = formatValidationError(validation.errors);
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Invalid Link",
              text: errorMessage,
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
      navigate(APP_ROUTES.homedashboard, { replace: true });
      setHasRedirected(true);
      return;
    }

    // Check if user is signed in
    if (!isSignedIn) {
      // User is not signed in - store redirect info and navigate to sign-in
      if (validation.module && validation.params) {
        saveRedirectLocation(validation.module, validation.params);
      }
      navigate(APP_ROUTES.signIn, { replace: true });
      setHasRedirected(true);
      return;
    }

    // User is signed in and we have valid params - handle redirect
    try {
      switch (validation.module) {
        case "project":
          if (validation.params) {
            handleProjectRedirect(
              validation.params as unknown as ProjectRedirectParams,
              navigate,
              dispatch
            );
            setHasRedirected(true);
          }
          break;

        // Future modules can be added here:
        // case "site":
        //   handleSiteRedirect(validation.params, navigate, dispatch);
        //   break;

        default:
          toast(
            <ToastMessage
              toastData={[
                {
                  title: "Invalid Link",
                  text: "The link you followed is invalid. Please check the URL and try again.",
                },
              ]}
              className="ToastError"
            />,
            {
              ...toastConfig,
            }
          );
          navigate(APP_ROUTES.homedashboard, { replace: true });
          setHasRedirected(true);
      }
    } catch (error) {
      console.error("Redirect error:", error);
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Error",
              text: "Something went wrong. Please try again or contact support if the issue persists.",
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
      navigate(APP_ROUTES.homedashboard, { replace: true });
      setHasRedirected(true);
    }
  }, [searchParams, navigate, dispatch, hasRedirected, isSignedIn]);

  return { isRedirecting: !hasRedirected };
};
